import React from "react";
import Title from "../../ui/Title";

const EnvironmentalRelations = () => {
  return (
    <div id="environmental-relations" className="scroll-mt-24">
      <Title
        title="Impact of climate change on resource availability"
        className="mb-6"
      />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          <a
            href="https://policy-practice.oxfam.org/resources/caring-in-a-changing-climate-centering-care-work-in-climate-action-621353/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            The paper
          </a>{" "}
          <i>
            Caring in a changing climate: Centering care work in climate action
          </i>{" "}
          explains in detail how climate change impacts human health and how
          caregivers face greater exposure to health risks due to climate
          impacts. Climate change also increases the complexity and frequency of
          care needs. For example, changing seasonal patterns and uncertainties
          affect the organization and intensity of care work in low-income
          families. Men migrating for work due to climate-related impacts leave
          women to assume household leadership roles. For example, our research
          in Nepal documented the experience of a woman farmer in a small
          village, whose husband, a truck driver in India, contracted HIV and
          transmitted it to his wife and children. He later passed away, leaving
          the family to fend for themselves with drought-hit farmland that had
          not yielded anything for almost a year.
        </p>

        <p>
          There is also an increase in cardiovascular diseases due to increased
          heat. While not always directly attributed to climate change,
          contributing factors such as increased burning and smoke worsen air
          pollution, leading to higher rates of respiratory conditions. The
          incidence of stroke has also risen due to heat exposure and
          dehydration.
        </p>

        <p className="italic">
          The direct and indirect effects of climate change result in the
          following:
        </p>

        <div className="space-y-2">
          <p className="italic">Food and water Insecurity</p>
          <p>
            Food and water insecurity increase care responsibilities and
            financial strain on caregivers. Climate change affects normal
            agricultural yield due to droughts and other seasonal patterns,
            while natural disasters destroy land, leading to a lack of
            sufficient food, resulting in malnutrition. Lack of access to
            adequate drinking water is another situation which is worsening with
            climate change. Water scarcity affects sanitation and hygiene,
            increasing care demands that include the procurement of clean water
            from external sources and care for hygiene-related illnesses.
          </p>
        </div>

        <div className="space-y-2">
          <p className="italic">Energy insecurity</p>
          <p>
            1.3 billion people lack access to fuel and electricity globally. The
            absence of clean fuel and energy leads people to spend more time on
            procuring these, having to pay more, and also sometimes having to
            deal with treacherous and dangerous conditions in the process. This
            also increases reliance on traditional energy sources such as
            firewood and coal, where women, who are often primary caregivers,
            face health risks from exposure.
          </p>
        </div>

        <div className="space-y-2">
          <p className="italic">Climate-induced migration and displacement</p>
          <p>
            Migration shifts care responsibilities onto those who remain behind,
            most often women, as men are more likely to migrate for work.
            Climate change drives migration, particularly in vulnerable regions.
            Women migrating for care jobs face exploitative working conditions
            and unmet family needs due to their unavailability at home.
          </p>
        </div>

        <p className="font-bold mt-4">
          Direct impact of climate change on care-related infrastructure and
          weakening of support networks
        </p>
        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Climate-induced debt crises hinder investment in care services.
          </li>
          <li>
            Rising public debt limits the ability of LMICs to fund care systems.
          </li>
          <li>
            Weak social protection systems reduce resilience to climate
            disasters.
          </li>
          <li>Migration erodes community cooperation in environmental care.</li>
          <li>
            Damage to care-related infrastructure such as hospital and other
            healthcare services increases care workloads.
          </li>
          <li>
            Climate disasters impact schools and other care facilities,
            affecting caregivers.
          </li>
        </ul>

        <p>
          There are several other issues which directly or indirectly manifest
          due to climate change, contributing to increased poverty,
          malnutrition, poor physical and mental health, and wellbeing. Scarcity
          due to extreme conditions shatter the foundation of vulnerable
          communities. Death and illness from increasingly frequent extreme
          weather events, such as heatwaves, storms and floods, along with the
          disruption of food systems, rising incidences of zoonotic, food-,
          water-, and vector-borne diseases, and mental health issues, further
          deepen the care crisis. Furthermore, climate change is undermining
          many of the social determinants for good health, such as livelihoods,
          equality, and access to healthcare and social support structures.
          These climate-sensitive health risks are disproportionately felt by
          the most vulnerable and disadvantaged, including women, children,
          ethnic minorities, low-income communities, migrants or displaced
          persons, older populations, and those with underlying health
          conditions.
        </p>

        <div className="flex flex-col items-center justify-center my-6">
          <img
            src="/assets/img/fact-sheet.png"
            alt="An overview of climate-sensitive health risks, their exposure pathways and vulnerability factors"
            className=""
          />
          <p className="mt-2 text-black italic">
            Source: An overview of climate-sensitive health risks, their
            exposure pathways and vulnerability factors. Climate change impacts
            health both directly and indirectly, and is strongly mediated by
            environmental, social and public health determinants (
            <a
              href="https://www.who.int/news-room/fact-sheets/detail/climate-change-and-health#:~:text=It%20severely%20jeopardizes%20the%20realization,when%20they%20are%20most%20needed."
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              WHO fact sheet
            </a>
            )
          </p>
        </div>

        <p>
          Another notable effect of climate change towards health and the care
          sector is how it affects achieving{" "}
          <b>universal health coverage (UHC)</b>. The compounding effect of
          increased disease burden exacerbates existing barriers to accessing
          health services and infrastructure, which results in major roadblocks
          to UHC. Regions most vulnerable to climate change often have the
          lowest UHC coverage.
        </p>

        <Title
          title="Recommendations for caregivers on preparing for climate change"
          className="mb-6"
        />

        <p>
          The Society of Behavioral Medicine has put together a{" "}
          <a
            href="https://www.sbm.org/healthy-living/how-to-prepare-for-climate-change-as-a-caregiver"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            list of recommendations
          </a>{" "}
          on preparing for climate change as a caregiver, including:
        </p>

        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Considering the potential impact of climate change in the area,
            including flooding, extreme heat, and water quality and supply
            issues.
          </li>
          <li>
            Making an emergency plan for the dependent, such as storing
            medication and medical records in an accessible way, planning to
            evacuate in case of adverse conditions, storing drinking water, and
            creating a support network/list of contact numbers in case of an
            emergency.
          </li>
          <li>
            Advocating with local representatives to make changes such as
            clearing stormwater drains or maintenance of power grids to prevent
            flooding or power failure.
          </li>
        </ul>

        <p>
          The Government of India has also put out a{" "}
          <a
            href="https://ncdc.mohfw.gov.in/wp-content/uploads/2024/07/4-Climate-change-and-Mental-Health-Role-of-Caregivers-for-Persons-with-Pre-Existing-Mental-Illness.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            short brochure
          </a>{" "}
          on supporting persons with mental health illness during climate change
          events. UNICEF has also put out a{" "}
          <a
            href="https://www.unicef.org/parenting/mental-health/climate-anxiety"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            guide
          </a>{" "}
          for parents on handling climate anxiety among children.
        </p>
      </div>
    </div>
  );
};

export default EnvironmentalRelations;
